import React, { FC, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { LocalizationProvider, ThemeProvider, Themes } from "@deere/isg.component-library";
import {
  Crawler,
  Dumptruck,
  Excavator,
  IconAutomation,
  LocationPin,
  Loader,
  SkidSteer,
  StatusAutonomyActive,
  StatusAutonomyOff,
  StatusAutonomyReady,
  StatusEquipmentIdle,
  StatusEquipmentWorking,
  StopCircle,
  Truck,
} from "@deere/icons/new";
import { Button, Dialog } from "@deere/isg.component-library";

import Header from "./header/Header";

import EngineeringTools from "./engineeringTools/EngineeringTools";
import {
  enterFullscreen,
  exitFullscreen,
  addFullscreenChangeListener,
  isFullscreen,
} from "./utils/fullscreenUtils";
import { hasPermission, Role } from "../permissions";
import { getNavigatorState } from "../modules/navigatorStateHelper";

import "./Interface.css";
import Sidebar from "./sidebar/Sidebar";
import MapInterface from "./mapInterface/MapInterface";
import { APIProvider } from "@vis.gl/react-google-maps";
import { AppContext, AppState } from "./context/AppContext";
import { PendingCommandsProvider } from "./sidebar/panels/equipmentPanel/tools/autonomy/PendingCommandsContext";
import type { MachinePath } from "./sidebar/panels/equipmentPanel/tools/autonomy/PendingCommandsContext";

// orgName={orgName} setOrgName={setOrgName} fullMQTT={mqttData} OrgData={orgData}
interface Props {
  fullMQTT: any;
  userName: any;
  orgName: string;
  setOrgName: (orgName: string) => void;
}

// Define an interface for the equipment item
interface EquipmentItem {
  position: {
    lat: number;
    lng: number;
  };
  referenceDiv: React.JSX.Element; // Use JSX.Element for React components
  callback: () => void;
  isMachineActive: boolean;
}

// Define a point for the point item
interface PointItem {
  position: {
    lat: number;
    lng: number;
  };
  referenceDiv: React.JSX.Element; // Use JSX.Element for React components
  callback: () => void;
}

// Define an interface for the Geometry item
export interface GeometryItem {
  geometry: Array<any>;
  callback: () => void;
}

// This is mapping variables, which are used to store live data of the user location
interface UserLocation {
  latitude: any;
  longitude: any;
}

/**
 * Cached vehicle marker icon builder.
 *
 * The marker icon (vehicle SVG + autonomy-status overlay) depends ONLY on the
 * vehicle type, whether the heading is flipped (heading > 180), the current
 * autonomy state, and the navigator state. Building this JSX tree for every
 * vehicle on every telemetry tick (~20 Hz) was a dominant main-thread cost.
 *
 * Keying on those four inputs lets us reuse the exact same element tree across
 * ticks whenever the visual is unchanged (the common case — heading rarely
 * crosses 180° and state changes are infrequent), which is behavior-identical
 * but avoids re-creating the SVG element trees on every position update.
 * The combination space is small and bounded, so the cache never grows large.
 */
const vehicleIconCache = new Map<string, React.JSX.Element>();

function buildVehicleIcon(
  vehicleType: string,
  headingFlipped: boolean,
  currentState: number,
  navigatorState: string | undefined,
): React.JSX.Element {
  const cacheKey = `${vehicleType}|${headingFlipped ? 1 : 0}|${currentState}|${navigatorState}`;
  const cached = vehicleIconCache.get(cacheKey);
  if (cached) return cached;

  const tempAutonomyStatus = (
    <>
      <div
        style={{
          position: "absolute",
          zIndex: 1,
          top: "0px",
          left: headingFlipped ? "24px" : "0px",
        }}
      >
        {currentState !== 1 && currentState !== 2 && (
          <div
            style={{
              width: "68px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              marginRight: "-23px",
            }}
          >
            <StatusAutonomyOff size={20} sx={{ marginRight: "4px" }} />
          </div>
        )}
        {currentState === 1 && (
          <div
            style={{
              width: "60px",
              color: "#00B600",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
            }}
          >
            <StatusAutonomyReady size={20} />
          </div>
        )}
        {currentState === 2 && navigatorState === "INTERRUPTED" && (
          <div
            style={{
              color: "rgb(255, 120, 0)",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
            }}
          >
            <StatusAutonomyActive size={20} sx={{ marginRight: "-10px" }} />
            <StatusEquipmentIdle size={20} sx={{ marginRight: "4px" }} />
          </div>
        )}
        {currentState === 2 && navigatorState !== "INTERRUPTED" && (
          <div
            style={{
              color: "#00B600",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
            }}
          >
            <StatusAutonomyActive size={20} sx={{ marginRight: "-10px" }} />
            <StatusEquipmentWorking size={20} sx={{ marginRight: "4px" }} />
          </div>
        )}
      </div>
    </>
  );

  let tempIcon = (
    <>
      <Dumptruck
        style={{
          height: "48px",
          width: "48px",
          transform: headingFlipped ? "scaleX(-1)" : "scaleX(1)",
        }}
      />
      {tempAutonomyStatus}
    </>
  );
  if (vehicleType === "DEX") {
    tempIcon = (
      <>
        <Excavator style={{ minHeight: "45px", minWidth: "45px", height: "auto", width: "auto" }} />
        {tempAutonomyStatus}
      </>
    );
  } else if (vehicleType === "CRW") {
    tempIcon = (
      <>
        <Crawler style={{ minHeight: "45px", minWidth: "45px", height: "auto", width: "auto" }} />
        {tempAutonomyStatus}
      </>
    );
  } else if (vehicleType === "CTL") {
    tempIcon = (
      <>
        <SkidSteer style={{ minHeight: "45px", minWidth: "45px", height: "auto", width: "auto" }} />
        {tempAutonomyStatus}
      </>
    );
  } else if (vehicleType === "TRK") {
    tempIcon = (
      <>
        <Truck style={{ minHeight: "45px", minWidth: "45px", height: "auto", width: "auto" }} />
        {tempAutonomyStatus}
      </>
    );
  } else if (vehicleType === "WDL") {
    tempIcon = (
      <>
        <Loader style={{ minHeight: "45px", minWidth: "45px", height: "auto", width: "auto" }} />
        {tempAutonomyStatus}
      </>
    );
  }

  const referenceDiv = <div style={{ minHeight: "45px", minWidth: "45px" }}>{tempIcon}</div>;
  vehicleIconCache.set(cacheKey, referenceDiv);
  return referenceDiv;
}

const backendURI = process.env.REACT_APP_STRIPPED_BACKEND_URI;

export const Interface: FC<Props> = (props: Props) => {
  const appContext: AppState = React.useContext(AppContext);
  const role = appContext.user.role;
  // console.log("User role in Interface component:", role);
  const [gjson, setGjson] = useState(); //stores upload GeoJSON to pass into the map interface

  // const [orgName, setOrgName] = useState<string>(appContext.selectedOrganization?.name || "");

  //const [orgName, setOrgName] = useState<string>("testORG");
  // console.log("Selected Organization from Context:", appContext.selectedOrganization?.name);
  // console.log("orgName in interface ", orgName);
  // console.log(" props.fullMQTT in interface ", props.fullMQTT)
  const [jobsiteName, setJobsiteName] = useState<string>(""); //Stores current active (clicked) jobsite name to pass into children
  const [isJobsiteUserSelected, setIsJobsiteUserSelected] = useState<boolean>(false); //Track if jobsite was explicitly selected by user
  const [jobsiteRefreshKey, setJobsiteRefreshKey] = useState<number>(0); //A variable to change which forces a rerender of jobsite
  const [sessionName, setSessionName] = useState<string>(""); //stores the current selected data session name
  const [sessionRefreshKey, setSessionRefreshKey] = useState<number>(0); //A variable to change which forces a rerender of session
  // Pending navigation request triggered by "Go to machine" toast button.
  // Consumed by WelcomePanel which calls its existing handleCurEquipment.
  const [pendingNav, setPendingNav] = useState<{
    jobsite: string;
    session: string;
    equipmentId: string;
  } | null>(null);
  const [orgData, setOrgData] = useState<any>({}); //stores organization data from backend
  const [orgDataSession, setOrgDataSession] = useState<any>({}); //stores the current data from the selected session
  // Tracks the inputs the map-building effect last processed so we can skip
  // rebuilding the equipment/geometry lists when the data that actually drives
  // the output is unchanged. orgData churns on every telemetry tick for the
  // whole org, while single-session view only depends on orgDataSession.
  const lastMapBuildInputRef = useRef<{ showAll: boolean; input: unknown }>({
    showAll: true,
    input: null,
  });
  // const [fullMQTT, setFullMQTT] = useState<any({});
  // const initialSessionName = Object.entries(Object.values(orgData)[0] as Array<Object>)[0][0]; //This is the initial session name
  // const [orgDataSessionName, setOrgDataSessionName] = useState(""); //stores the current selected data session name
  const [mapEquipmentList, setMapEquipmentList] = useState<EquipmentItem[]>([]); // Stores all of the items that are to be added to the map
  const [mapGeometryList, setMapGeometryList] = useState<GeometryItem[]>([]);
  const [mapPointList, setMapPointList] = useState<PointItem[]>([]);
  const [showAllVehicles, setShowAllVehicles] = useState(true);
  const [totalEquipmentList, setTotalEquipmentList] = useState([]); // Used for the sidepanel information
  const [followedVehicleId, setFollowedVehicleId] = useState<string | null>(null);
  const [showCabControl, setShowCabControl] = useState(false);
  // used for broadcasting information
  const [currentTopicString, setCurrentTopicString] = useState("");

  const { socket } = React.useContext(AppContext);

  useEffect(() => {
    if (!socket) return;

    if (showCabControl && currentTopicString) {
      const topicPrefix = currentTopicString.endsWith("/")
        ? currentTopicString.slice(0, -1)
        : currentTopicString;
      socket.emit("enterCabControl", { topicPrefix });
    } else {
      socket.emit("leaveCabControl");
    }
  }, [showCabControl, currentTopicString, socket]);

  const showCabControlRef = useRef(showCabControl);
  const currentTopicStringRef = useRef(currentTopicString);
  useEffect(() => {
    showCabControlRef.current = showCabControl;
  }, [showCabControl]);
  useEffect(() => {
    currentTopicStringRef.current = currentTopicString;
  }, [currentTopicString]);

  useEffect(() => {
    if (!socket) return;
    const handleSocketReconnect = () => {
      if (showCabControlRef.current && currentTopicStringRef.current) {
        const topicPrefix = currentTopicStringRef.current.endsWith("/")
          ? currentTopicStringRef.current.slice(0, -1)
          : currentTopicStringRef.current;
        socket.emit("enterCabControl", { topicPrefix });
      }
    };
    socket.on("connect", handleSocketReconnect);
    return () => {
      socket.off("connect", handleSocketReconnect);
    };
  }, [socket]);

  const [vehicleIconClicked, setVehicleIconClicked] = useState(false);
  const [vehicleData, setVehicleData] = useState<any>(null);
  const [showGeometryOnMap, setShowGeometryOnMap] = useState<boolean>(false);
  // Tracks machine count for the current context: org-wide before jobsite selection, then jobsite/session-specific after explicit user selection
  const [machineCount, setMachineCount] = useState(0);
  const autoSetOrg = false;

  const [userLocation, setUserLocation] = useState<UserLocation>({
    latitude: 42.568132945312826,
    longitude: -90.68865022456608,
  });
  const [isSpoofing, setIsSpoofing] = useState<boolean>(false);
  const isSpoofingRef = useRef(isSpoofing);
  useEffect(() => {
    isSpoofingRef.current = isSpoofing;
  }, [isSpoofing]);
  const [originalLocation, setOriginalLocation] = useState<UserLocation | null>(null);

  // Mapping interface
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

  // Add state for selected feature
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);

  // Mission preview state
  const [missionPreviewActive, setMissionPreviewActive] = useState<boolean>(false);
  const [missionPreviewPaths, setMissionPreviewPaths] = useState<any[]>([]);
  const [missionPreviewPoints, setMissionPreviewPoints] = useState<any[]>([]);

  // All Stop dialog state
  const [allStopDialogOpen, setAllStopDialogOpen] = useState(false);
  const [allStopLoading, setAllStopLoading] = useState(false);
  const [allStopError, setAllStopError] = useState<string | null>(null);

  const [initialMapView, setInitialMapView] = useState<
    | {
        center: google.maps.LatLngLiteral;
        zoom: number;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    if (mapInstance && initialMapView === undefined) {
      const center = mapInstance.getCenter();
      const zoom = mapInstance.getZoom();

      if (center && zoom !== undefined && zoom !== null) {
        const view = {
          center: center.toJSON(),
          zoom,
        };
        setInitialMapView(view);
      }
    }
  }, [mapInstance, initialMapView]);

  const handleJobsitePanelClose = () => {
    if (!mapInstance || !initialMapView) {
      return;
    }
    mapInstance.setZoom(initialMapView.zoom);
    mapInstance.panTo(initialMapView.center);
    setIsJobsiteUserSelected(false);
  };

  // Triggered from "Go to machine" toast action. Cross-org navigation is intentionally NOT supported —
  // org switch already cancels pending commands.
  const navigateToMachine = useCallback(
    (path: MachinePath) => {
      if (path.org !== props.orgName) return;
      setJobsiteName(path.jobsite);
      setIsJobsiteUserSelected(true);
      setSessionName(path.session);
      setPendingNav({
        jobsite: path.jobsite,
        session: path.session,
        equipmentId: `${path.machineType}_${path.machineModel}_${path.machineName}`,
      });
    },
    [props.orgName],
  );

  useEffect(() => {}, [userLocation]);

  // useEffect(() => {
  //     console.log([props.fullMQTT]);
  // }, [props.fullMQTT])

  // Starts watching the users location
  useEffect(() => {
    // Check if Geolocation is supported
    if ("geolocation" in navigator) {
      const updateLocation = (position: GeolocationPosition) => {
        console.log("Geolocation update received:", {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
        // Don't overwrite spoofed location with real GPS data
        if (isSpoofingRef.current) return;
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        } as UserLocation);
      };

      const errorHandler = (error: GeolocationPositionError) => {
        console.error("Error getting location:", error);
        console.error("Geolocation error code:", error.code, "message:", error.message);
      };

      // Watch the position for real-time updates
      const watchId = navigator.geolocation.watchPosition(updateLocation, errorHandler, {
        enableHighAccuracy: true, // Request high accuracy
        maximumAge: 0, // Do not use cached position
        timeout: 10000, // Timeout after 10 seconds
      });

      console.log("Geolocation watch started, watchId:", watchId);

      // Cleanup function to stop watching when the component unmounts
      return () => {
        navigator.geolocation.clearWatch(watchId);
        console.log("halting geolocation, cleared watchId:", watchId);
      };
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  }, []);

  // This is where all of the new data structure will go

  // This is the main updating info function. This should be the only place where orgData and orgDataSession are set
  function updateInfo() {
    // console.log("Updating Info");
    // check if there are names for org, jobsite, session and that they work
    if (props.orgName && jobsiteName && sessionName) {
      if (
        Object.keys(props.fullMQTT).includes(props.orgName) && //Checks for valid orgName
        Object.keys(props.fullMQTT[props.orgName]).includes(jobsiteName) && //Checks for valid jobsiteName
        Object.keys(props.fullMQTT[props.orgName][jobsiteName]).includes(sessionName) // Checks for valid sessionName
      ) {
        // sets orgData
        setOrgData(props.fullMQTT[props.orgName]);
        // sets orgDataSession
        setOrgDataSession(props.fullMQTT[props.orgName][jobsiteName][sessionName]);
        // sets topic
        // setCurrentTopicString(orgName + "/" + jobsiteName + "/" + sessionName + "/");
        setCurrentTopicString(props.orgName + "_" + jobsiteName + "_" + sessionName + "/");
      } else {
        console.log(
          "Error: improper names/directory for: org, jobsite, session, Attempting restart...",
        );
        // Start a small loop to check if the org values are good. If the loop fails, reset the org values
        let checkIndex = 0;

        // function forceOrgNames() {
        //     if (orgName !== null && !Object.keys(props.fullMQTT).includes(orgName)) {
        //         setOrgName(Object.keys(props.fullMQTT)[0][0]); //Attempting to fix by jump starting org naming
        //         console.log("Error: orgName is not in fullMQTT");
        //         console.log(Object.keys(props.fullMQTT));
        //         console.log(orgName);
        //     } else if (jobsiteName !== null && !Object.keys(props.fullMQTT[orgName]).includes(jobsiteName)) {
        //         setJobsiteName(Object.keys(props.fullMQTT[orgName])[0][0]); //Attempting to fix by jump starting jobsite naming
        //         console.log("Error: jobsiteName is not in fullMQTT");
        //         console.log(Object.keys(props.fullMQTT[orgName]));
        //         console.log(jobsiteName);
        //     } else if (sessionName !== null && !Object.keys(props.fullMQTT[orgName][jobsiteName]).includes(sessionName)) {
        //         setSessionName(Object.keys(props.fullMQTT[orgName][sessionName])[0][0]); //Attempting to fix by jump starting session naming
        //         console.log("Error: sessionName is not in fullMQTT");
        //         console.log(Object.keys(props.fullMQTT[orgName][jobsiteName]));
        //         console.log(sessionName);
        //     } else {
        //         // Lots going wrong if we reach here. Attempting a reset of the org
        //         let orgEntries = Object.entries(props.fullMQTT);
        //         if (orgEntries.length > 0) {
        //             setOrgName(orgEntries[0][0]); //Updates the orgName to be the first value
        //         }
        //     }
        // }

        function forceOrgNames() {
          // Check if orgName is valid
          if (props.orgName != null && !Object.keys(props.fullMQTT).includes(props.orgName)) {
            console.log("Error: orgName is not in fullMQTT");
            console.log(Object.keys(props.fullMQTT));
            console.log(props.orgName);

            // Set orgName to the first available organization
            const validOrgNames = Object.keys(props.fullMQTT);
            if (validOrgNames.length > 0) {
              props.setOrgName(validOrgNames[0]); // Set to the first valid organization name
            }
            return; // Exit the function as we’ve handled the error
          }

          // Check if jobsiteName is valid
          if (
            jobsiteName !== null &&
            !Object.keys(props.fullMQTT[props.orgName] || {}).includes(jobsiteName)
          ) {
            console.log("Error: jobsiteName is not in fullMQTT");
            console.log(Object.keys(props.fullMQTT[props.orgName] || {}));
            console.log(jobsiteName);

            // Set jobsiteName to the first available jobsite for the current orgName
            const validJobsiteNames = Object.keys(props.fullMQTT[props.orgName] || {});
            if (validJobsiteNames.length > 0) {
              setJobsiteName(validJobsiteNames[0]); // Set to the first valid jobsite name
              setIsJobsiteUserSelected(false);
            }
            return; // Exit the function as we’ve handled the error
          }

          // Check if sessionName is valid
          if (
            sessionName !== null &&
            !Object.keys(props.fullMQTT[props.orgName]?.[jobsiteName] || {}).includes(sessionName)
          ) {
            console.log("Error: sessionName is not in fullMQTT");
            console.log(Object.keys(props.fullMQTT[props.orgName]?.[jobsiteName] || {}));
            console.log(sessionName);

            // Set sessionName to the first available session for the current jobsite
            const validSessionNames = Object.keys(
              props.fullMQTT[props.orgName]?.[jobsiteName] || {},
            );
            if (validSessionNames.length > 0) {
              setSessionName(validSessionNames[0]); // Set to the first valid session name
            }
            return; // Exit the function as we’ve handled the error
          }

          // If we reach here, everything is valid, but we might still want to ensure the hierarchy is correct.
          // Only reset orgName when it is missing or no longer present in fullMQTT —
          // otherwise this would clobber a deliberate selection (e.g. Dev Tools) and cause it to flip
          // back to the first/header org.
          const orgEntries = props.fullMQTT ? Object.entries(props.fullMQTT) : [];
          const orgNameValid = props.orgName && Object.keys(props.fullMQTT).includes(props.orgName);
          if (orgEntries.length > 0 && !orgNameValid) {
            const firstOrg = orgEntries[0][0];
            props.setOrgName(firstOrg);

            // Set jobsiteName and sessionName if undefined
            const firstJobsite = Object.keys(props.fullMQTT[firstOrg] || [])[0];
            if (firstJobsite) {
              setJobsiteName(firstJobsite);

              const firstSession = Object.keys(props.fullMQTT[firstOrg][firstJobsite] || [])[0];
              if (firstSession) {
                setSessionName(firstSession);
              }
            }
          }
        }

        async function checkOrgValues() {
          if (
            Object.keys(props.fullMQTT).includes(props.orgName) && //Checks for valid orgName
            Object.keys(props.fullMQTT[props.orgName]).includes(jobsiteName) && //Checks for valid jobsiteName
            Object.keys(props.fullMQTT[props.orgName][jobsiteName]).includes(sessionName) // Checks for valid sessionName
          ) {
            return;
          }

          // Wait 100 miliseconds then restart for 5 cycles
          await new Promise((r) => setTimeout(r, 100));
          checkIndex += 1;
          if (checkIndex < 5) {
            checkOrgValues();
          } else {
            // Forcing fix
            forceOrgNames();
          }
        }

        checkOrgValues();
      }
    } else {
      // Org, jobsite, or session name is not yet available.
      // When org data arrives, the useEffect on props.fullMQTT will initialise them.
      if (props.orgName && props.fullMQTT[props.orgName] && (!jobsiteName || !sessionName)) {
        // Data exists but jobsite/session haven't been picked yet — let handleOrgNameUpdate seed them.
        handleOrgNameUpdate(props.orgName);
      }
    }
  }

  function handleOrgNameUpdate(newOrgName: string) {
    if (newOrgName) {
      setIsJobsiteUserSelected(false);
      // In the future need to check if it is a valid org structure before selecting the org
      if (props.fullMQTT[newOrgName] && Object.keys(props.fullMQTT[newOrgName]).length > 0) {
        let orgNameObjectEntries = Object.keys(props.fullMQTT[newOrgName]);
        // The orgName is valid
        // check if jobsiteName will be changing
        if (jobsiteName == orgNameObjectEntries[0][0]) {
          // No need to change the jobsiteName, force refreshing jobsiteName
          setJobsiteRefreshKey((prev) => prev + 1);
        } else {
          const orgData = props.fullMQTT?.[newOrgName];
          if (orgData && Object.keys(orgData).length > 0) {
            setJobsiteName(Object.entries(orgData)[0][0]); //Updates jobsiteName to be the first value
          }
        }
      } else {
        // Data for the new org hasn't arrived yet (socket request is in-flight).
        // Clear stale jobsite/session state; the useEffect on props.fullMQTT will
        // re-initialise everything once the data comes in.
        setJobsiteName("");
        setSessionName("");
        setOrgData({});
        setOrgDataSession({});
      }
    }
  }

  // Tracks whether Dev Tools has overridden the org selection.
  const [devToolsOrgOverride, setDevToolsOrgOverride] = useState(false);

  // handles updates to orgName from appContext.selectedOrganization
  useEffect(() => {
    // Skip if Dev Tools has overridden the org.
    if (devToolsOrgOverride) return;
    const newName = appContext.selectedOrganization?.name || "";
    if (newName !== props.orgName) {
      props.setOrgName(newName);
      handleOrgNameUpdate(newName);
    }
  }, [appContext.selectedOrganization, devToolsOrgOverride]);
  // handles updates to orgName from setOrgName
  useEffect(() => {
    // sets the jobsite name to the default name
    handleOrgNameUpdate(props.orgName);
    // Eagerly update orgData so the jobsite list reflects the new org.
    // If the new org data hasn't arrived yet, clear stale data.
    if (props.orgName && props.fullMQTT[props.orgName]) {
      setOrgData(props.fullMQTT[props.orgName]);
    } else {
      setOrgData({});
    }

    // Always disable spoofing when switching organizations
    if (isSpoofing) {
      setIsSpoofing(false);
      if (originalLocation) {
        setUserLocation({ ...originalLocation });
      }
      setOriginalLocation(null);
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setUserLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          },
          (err) => {
            console.warn(
              "[Spoof] Could not get fresh browser location on org switch:",
              err.message,
            );
          },
        );
      }
    }
  }, [props.orgName]);

  // handles updates to the jobsiteName
  useEffect(() => {
    if (
      props.orgName &&
      jobsiteName &&
      props.fullMQTT?.[props.orgName] &&
      Object.keys(props.fullMQTT[props.orgName]).includes(jobsiteName)
    ) {
      if (
        props?.fullMQTT?.[props.orgName]?.[jobsiteName] &&
        sessionName == Object?.entries(props?.fullMQTT?.[props.orgName]?.[jobsiteName])?.[0]?.[0]
      ) {
        // No need to change the sessionName, force refreshing sessionName
        setSessionRefreshKey((prev) => prev + 1);
      } else {
        const sessionData = props.fullMQTT?.[props.orgName]?.[jobsiteName];
        if (sessionData && Object.keys(sessionData).length > 0) {
          setSessionName(Object.entries(sessionData)[0][0]); //Updates sessionName to be the first value
        }
      }
    }
  }, [jobsiteName, jobsiteRefreshKey]);
  // handles updates to the sessionName or jobsiteName
  useEffect(() => {
    if (sessionName) {
      updateInfo();
    }
  }, [sessionName, sessionRefreshKey, jobsiteName]);

  // handles updates to the mqtt data
  useEffect(() => {
    // if no org exists, update the org
    if (props.orgName && Object.keys(props.fullMQTT).includes(props.orgName)) {
      // Always update orgData so the jobsite list refreshes even if
      // jobsiteName/sessionName haven't been resolved for the new org yet.
      setOrgData(props.fullMQTT[props.orgName]);
      updateInfo();
    } else {
      let orgEntries = props.fullMQTT ? Object.entries(props.fullMQTT) : [];
      if (orgEntries.length > 0) {
        if (autoSetOrg) {
          props.setOrgName(orgEntries[0][0]); //Updates the orgName to be the first value
        }
      }
    }
  }, [props.fullMQTT]);

  // Handles updates from orgData for mapping variables
  useEffect(() => {
    // Skip the rebuild when the data this effect actually consumes is unchanged.
    // In single-session view the output depends only on orgDataSession; orgData
    // changes on every telemetry tick for the whole org, which would otherwise
    // force a full equipment/geometry rebuild ~20x/sec for no visible change.
    // Structural sharing guarantees these refs change whenever the underlying
    // data changes, and the periodic snapshot refreshes them as a safety net.
    const relevantBuildInput = showAllVehicles ? orgData : orgDataSession;
    const prevBuildInput = lastMapBuildInputRef.current;
    if (
      prevBuildInput.showAll === showAllVehicles &&
      Object.is(prevBuildInput.input, relevantBuildInput)
    ) {
      return;
    }
    lastMapBuildInputRef.current = { showAll: showAllVehicles, input: relevantBuildInput };

    // Add the neccessary equipment to totalEquipment (this is used for the sidepanel info)
    type OutputFormat = {
      [vehicleIndex: string]: {
        vehicleName: string;
        vehicleType: string;
        vehicleModel: string;
        data: object;
      };
    };
    let tempTotalEquipmentList: OutputFormat = {};

    // Add all of the neccessary mapping information
    let tempEquipmentList: any[] = [];
    let tempGeometryList: any = {
      Shapes: Array<any>,
      Lines: Array<any>,
      Points: Array<any>,
    };
    tempGeometryList.Shapes = [];
    tempGeometryList.Lines = [];
    let auxLines: any[] = [];
    tempGeometryList.Points = [];
    let tempPointList: any[] = [];

    // This checks if the sessionItem belongs in either temporary vehicle or map equipment list and adds it
    function checkSessionItem(
      sessionItem: any,
      sessionItemIndex: any,
      jobsiteNameForItem?: string,
    ) {
      // verify that the element is one of: vehicle / Shape / Line / Point
      const capitalLetterCount = (sessionItemIndex.match(/[A-Z]/g) || []).length;
      if (capitalLetterCount == 3) {
        // Item is a vehicle
        // Iterate through each Vehicle Model
        for (const vehicleModel in sessionItem) {
          const vehicleNames = sessionItem[vehicleModel];
          // Iterate through each Vehicle Name
          for (const vehicleName in vehicleNames) {
            const data = vehicleNames[vehicleName];
            // Construct the new format
            // let vehicleIndex = sessionItemIndex + "/" + vehicleModel + "/" + vehicleName;
            let vehicleIndex = sessionItemIndex + "_" + vehicleModel + "_" + vehicleName;
            tempTotalEquipmentList[vehicleIndex] = {
              vehicleName: vehicleName,
              vehicleType: sessionItemIndex, //This is the vehicle type
              vehicleModel: vehicleModel,
              data: data,
            };

            // const { position, referenceDiv, callback } = item
            // lat: 42.576170670703114,
            // lng: -90.70850521484375,
            const thisEquipment = {
              vehicleName: vehicleName,
              vehicleType: sessionItemIndex,
              vehicleModel: vehicleModel,
              data: data as any,
            };
            // console.log(thisEquipment);
            let thisLat = -300;
            let thisLng = -300;
            let thisHeading = 0; //heading in degrees
            // try to find lat long
            try {
              // Check if the different location formats on the vehicles
              if (
                thisEquipment.data.MachineState !== undefined &&
                thisEquipment.data.MachineState.GlobalPose !== undefined
              ) {
                thisLat = thisEquipment.data.MachineState.GlobalPose.location.latitude;
                thisLng = thisEquipment.data.MachineState.GlobalPose.location.longitude;
                thisHeading = thisEquipment.data.MachineState.GlobalPose.location?.heading;
              } else if (thisEquipment.data.front_axle_center.GlobalPose !== undefined) {
                thisLat = thisEquipment.data.front_axle_center.GlobalPose.location.latitude;
                thisLng = thisEquipment.data.front_axle_center.GlobalPose.location.longitude;
                thisHeading = thisEquipment.data.front_axle_center.GlobalPose.location?.longitude;
              }
            } catch (error) {
              // console.log(error);
            }

            let currentState = Number((thisEquipment.data as any)?.VADC?.autonomy?.state?.[0]);
            // Navigator State
            let navigatorState = getNavigatorState(
              (thisEquipment.data as any)?.Navigator?.NavigatorState,
            );
            // Time since the last autonomy event

            let thisAutonomyState = (thisEquipment.data as any)?.autonomyLog?.[
              Object.keys((thisEquipment.data as any)?.autonomyLog).length - 1
            ];

            let state0 = thisAutonomyState?.["0"];

            const isMachineActive =
              currentState === 1 ||
              currentState === 2 ||
              currentState === 3 ||
              currentState === 255;

            // The icon JSX depends only on vehicle type, heading-flip, current
            // state and navigator state — build it through a cache so we don't
            // recreate the SVG element trees on every position-only tick.
            const headingFlipped = thisHeading > 180;
            const referenceDiv = buildVehicleIcon(
              thisEquipment.vehicleType,
              headingFlipped,
              currentState,
              navigatorState,
            );

            tempEquipmentList.push({
              position: {
                lat: thisLat,
                lng: thisLng,
              },
              referenceDiv: referenceDiv,
              callback: () => {
                setVehicleIconClicked(true);
                setVehicleData(thisEquipment);
                if (jobsiteNameForItem) {
                  setJobsiteName(jobsiteNameForItem);
                }
              },
              vehicleName: vehicleName,
              vehicleType: sessionItemIndex,
              vehicleModel: vehicleModel,
              isMachineActive: isMachineActive,
            });

            if (data?.Line) {
              // Check if it matches the nested structure for vehicle lines
              const nestedLine =
                data.Line?.[sessionItemIndex]?.[vehicleModel]?.[vehicleName]?.Navigator;

              if (nestedLine) {
                const lineName = `${sessionItemIndex}_${vehicleModel}_${vehicleName}`;
                auxLines.push({
                  [lineName]: {
                    ...nestedLine,
                    pathType: "VEHICLE_PATH",
                  },
                });
              } else {
                // Iterate over each key in data.Line and add the pathType property
                for (let key in data.Line) {
                  if (data.Line.hasOwnProperty(key)) {
                    data.Line[key].pathType = "VEHICLE_PATH"; // Add pathType to each line object
                  }
                }
                auxLines.push(data.Line);
              }
            }
          }
        }
      } else if (sessionItemIndex == "Shape") {
        tempGeometryList.Shapes.push(sessionItem);
        // console.log(sessionItem);
      } else if (sessionItemIndex == "Line") {
        // Iterate over each key in sessionItem and add the pathType property
        for (let key in sessionItem) {
          if (sessionItem.hasOwnProperty(key)) {
            sessionItem[key].pathType = "JOBSITE_PATH"; // Add pathType to each line object
          }
        }

        // Merge into Lines[0] so all lines are visible on the map.
        // Previously used push(), which created Lines[1], Lines[2], etc. that were never rendered.
        if (!tempGeometryList.Lines[0]) {
          tempGeometryList.Lines[0] = {};
        }
        tempGeometryList.Lines[0] = {
          ...tempGeometryList.Lines[0],
          ...sessionItem,
        };
      } else if (sessionItemIndex == "Point") {
        tempGeometryList.Points.push(sessionItem);

        for (const pointName in sessionItem) {
          const point = sessionItem[pointName];
          //point =  {
          //     "location": {
          //         "longitude": -90.71713451548769,
          //             "latitude": 42.584675748838585
          //     },
          //     "pointType": "DUMP_POINT",
          //         "material": {
          //         "type": "Gravel",
          //             "units": "yd^3",
          //                 "requested": 0,
          //                     "current": 0
          //     },
          //     "approachHeading": 1.06099789627e-313
          // }
          const thisPoint = {
            pointName: pointName,
            pointType: point.pointType,
            data: point as any,
          };

          let thisLat = point?.location?.latitude || -300;
          let thisLng = point?.location?.longitude || -300;

          let tempIcon = (
            <LocationPin
              style={{
                position: "relative",
                top: "22px",
                minHeight: "45px",
                minWidth: "45px",
                height: "auto",
                width: "auto",
              }}
            />
          );
          // tempGeometryList.Points.push(sessionItem);
          tempPointList.push({
            position: {
              lat: thisLat,
              lng: thisLng,
            },
            referenceDiv: <div style={{ minHeight: "25px", minWidth: "25px" }}>{tempIcon}</div>,
            callback: () => console.log(thisPoint.pointName),
          });
        }
      }

      // Add the ability to verify vehicle, shape, line, point
    }

    if (!showAllVehicles) {
      // Here we use orgDataSession
      // Iterate through each sessionItem
      for (const sessionItemIndex in orgDataSession) {
        checkSessionItem(orgDataSession[sessionItemIndex], sessionItemIndex, jobsiteName);
      }
    } else {
      // Here we use orgData
      // Iterate through each Jobsite
      for (const jobsiteNameIndex in orgData) {
        const jobsite = orgData[jobsiteNameIndex];
        // Iterate through each session
        for (const sessionNameIndex in jobsite) {
          const session = jobsite[sessionNameIndex];
          // Iterate through each sessionItem
          for (const sessionItemIndex in session) {
            checkSessionItem(session[sessionItemIndex], sessionItemIndex, jobsiteNameIndex);
          }
        }
      }
    }

    // console.log(tempGeometryList.Lines);
    // console.log(auxLines);

    // Need to organize shapes, lines by size
    setTotalEquipmentList(tempTotalEquipmentList as any);
    setMapEquipmentList(tempEquipmentList);
    setMachineCount(tempEquipmentList.length);

    // console.log(tempGeometryList);
    // Merge all auxLines
    let mergedAuxLines = {};
    for (const lineObj of auxLines) {
      mergedAuxLines = { ...mergedAuxLines, ...lineObj };
    }
    if (!tempGeometryList.Lines[0]) {
      tempGeometryList.Lines[0] = {};
    }
    tempGeometryList.Lines[0] = { ...tempGeometryList.Lines[0], ...mergedAuxLines };
    // console.log({ ...tempGeometryList.Lines[0], ...auxLines[0] });
    // console.log(tempGeometryList.Lines[0]);
    // console.log(tempGeometryList);

    setMapGeometryList(tempGeometryList);
    // setMapGeometryList({ ...tempGeometryList.Lines[0], ...auxLines[0] });

    setMapPointList(tempPointList);
  }, [orgData, showAllVehicles, orgDataSession]);

  function setGjsonWrapper(gjsonFile: any) {
    setGjson(gjsonFile);
  }

  // Whether cab control enters fullscreen mode
  const [cabControlFullscreen, setCabControlFullscreen] = useState(true);

  // Handeling the toggling of the Engineering Tools Interface
  const [showEngineeringTools, setShowEngineeringTools] = useState(false);
  const toggleEngineeringTools = () => {
    setShowEngineeringTools((prevState) => !prevState);
  };

  // Define state variables for zoom and translation
  const [isMini, setIsMini] = useState<boolean>(false);
  const [zoomPercent, setZoomPercent] = useState<number>(100);
  const [translateX, setTranslateX] = useState<number>(0);
  const [translateY, setTranslateY] = useState<number>(0);
  const [translateYHeader, setTranslateYHeader] = useState<number>(0);
  const [height, setHeight] = useState<number>(0);

  // Function to set scale and translate based on window width
  const setScaleTranslate = () => {
    const width = window.innerWidth;

    if (width < 421) {
      setIsMini(true);
    } else {
      setIsMini(false);
    }

    const newZoomPercent = (width / 420) * 100; // Calculate zoom percent

    setZoomPercent(newZoomPercent);

    // Set translate based on zoom level
    const factor = newZoomPercent / 100; // Convert to scale factor

    setTranslateX(-210 * (1 / factor - 1)); // Example translation value
    setTranslateY(-1 * (0.5 * (window.innerHeight - 96) + 96) * (1 / factor - 1)); // Example translation value
    // setHeight(window.innerHeight + (2*((window.innerHeight/2) +96) * ((1/factor)-1)))
    setHeight((window.innerHeight - 96) / factor);
    // setTranslateY(0);

    setTranslateYHeader(-48 * (1 / factor - 1)); // Example translation value

    // setTranslateX((420 - window.innerWidth) / 2); // Example translation value
    // setTranslateY((420 - window.innerHeight) / 2); // Example translation value
  };

  // Use effect to handle window resize
  useEffect(() => {
    setScaleTranslate(); // Set initial scale and translate on mount

    // Event listener for window resize
    window.addEventListener("resize", setScaleTranslate);
    return () => {
      // Clean up the event listener
      window.removeEventListener("resize", setScaleTranslate);
    };
  }, []);

  // Use effect to handle manual fullscreen exit (e.g., pressing ESC key)
  useEffect(() => {
    const handleFullscreenChange = () => {
      // If user manually exits fullscreen but Cab Control is still showing, close it
      if (!isFullscreen() && showCabControl) {
        setShowCabControl(false);
        setFollowedVehicleId(null);
      }
    };

    // Add listener for fullscreen change events
    const removeListener = addFullscreenChangeListener(handleFullscreenChange);

    return () => {
      // Clean up the event listener
      removeListener();
    };
  }, [showCabControl]);

  function setNewOrgName(newOrgName: string) {
    props.setOrgName(newOrgName);
  }

  function setDevToolsOrgName(newOrgName: string) {
    setDevToolsOrgOverride(true);
    props.setOrgName(newOrgName);
  }

  function revertToHeaderOrg() {
    setDevToolsOrgOverride(false);
    const headerOrg = appContext.selectedOrganization?.name || "";
    props.setOrgName(headerOrg);
    handleOrgNameUpdate(headerOrg);
  }

  async function toggleCabControl(vehicleName?: string) {
    if (showCabControl) {
      // Exiting Cab Control - exit fullscreen and stop following
      setShowCabControl(false);
      setFollowedVehicleId(null);
      try {
        if (isFullscreen()) {
          await exitFullscreen();
        }
      } catch (error) {
        console.error("Error exiting fullscreen:", error);
      }
    } else {
      // Entering Cab Control - enter fullscreen and auto-follow
      setShowCabControl(true);
      if (vehicleName) {
        setFollowedVehicleId(vehicleName);
      }
      if (cabControlFullscreen) {
        try {
          await enterFullscreen();
        } catch (error) {
          console.error("Error entering fullscreen:", error);
          // Even if fullscreen fails, we still want to show the Cab Control
        }
      }
    }
  }

  // All Stop handler
  const handleAllStop = async () => {
    setAllStopLoading(true);
    setAllStopError(null);
    try {
      const url = `https://${backendURI}/Autonomy/AllStop`;
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // body: JSON.stringify({ currentTopicString: `${props.orgName}_${jobsiteName}` })
        body: JSON.stringify({ currentTopicString: currentTopicString }),
      });
      if (!response.ok) throw new Error("Failed to send All Stop");
      setAllStopDialogOpen(false);
    } catch (err: any) {
      setAllStopError(err.message || "Unknown error");
    } finally {
      setAllStopLoading(false);
    }
  };

  // const calculateAllStopPosition = () => {
  //     if (showCabControl) {
  //         // Full map overlay mode (Cab Control active)
  //         return { top: 50, right: 5 };
  //     } else if (isMini) {
  //         // Mobile/small screen with header
  //         return { top: 70, right: 20 };
  //     } else {
  //         return { top: 10, right: 10 };
  //     }
  // };

  const isAllStopDisabled =
    props.orgName.trim() === "" || !isJobsiteUserSelected || machineCount === 0;
  // const allStopPosition = calculateAllStopPosition();

  const allStopOverlay = showCabControl ? (
    /* ── CarPlay-style: large circular Stop below Pause, Dev Tools + Close at top-right ── */
    <div className="AllStopOverlayCabControl">
      {/* Top-right utility buttons */}
      <div className="CabHUD-TopRight">
        {hasPermission(role, "ORCHESTRATOR_ENG_TOOLS") && (
          <button
            className="CabHUD-UtilBtn CabHUD-UtilBtn--devtools"
            onClick={toggleEngineeringTools}
          >
            <IconAutomation size={22} />
            Dev Tools
          </button>
        )}
      </div>
      {/* Large circular All Stop */}
      {hasPermission(role, "ALL_STOP") && (
        <div className="CabHUD-StopArea">
          <button
            className="CabHUD-CircleBtn CabHUD-CircleBtn--stop"
            disabled={isAllStopDisabled}
            onClick={() => setAllStopDialogOpen(true)}
          >
            <StopCircle size={56} />
            <span className="CabHUD-CircleBtn-label">All Stop</span>
          </button>
        </div>
      )}
    </div>
  ) : (
    /* ── Normal mode: small overlay buttons ── */
    <div className="AllStopOverlay" style={{ display: "flex", gap: "8px" }}>
      {hasPermission(role, "ORCHESTRATOR_ENG_TOOLS") && (
        <Button
          style={{ backgroundColor: "#367c2b", color: "white", width: 110, height: 40 }}
          onClick={toggleEngineeringTools}
        >
          <IconAutomation size={20} style={{ marginRight: "8px" }} />
          Dev Tools
        </Button>
      )}
      {hasPermission(role, "ALL_STOP") && (
        <Button
          disabled={isAllStopDisabled}
          style={{
            backgroundColor: "red",
            color: "white",
            width: 110,
            height: 40,
            opacity: isAllStopDisabled ? 0.5 : 1,
          }}
          onClick={() => setAllStopDialogOpen(true)}
        >
          <StopCircle size={20} style={{ marginRight: "8px" }} />
          All Stop
        </Button>
      )}
    </div>
  );

  // Memoize geometry merging so we don't create a new object every render
  const mergedGeometryForMap = useMemo(() => {
    const emptyGeometry = { Shapes: [], Lines: [], Points: [] };
    let geometryForMap: any = showGeometryOnMap ? mapGeometryList : emptyGeometry;

    if (missionPreviewActive && missionPreviewPaths.length > 0) {
      const missionLines: any = {};
      missionPreviewPaths.forEach((pathData: any, index: number) => {
        missionLines[`missionPath_${index}`] = pathData;
      });
      const existingLines = geometryForMap.Lines?.[0] || {};
      const mergedLines = { ...existingLines, ...missionLines };
      geometryForMap = {
        ...geometryForMap,
        Lines: [mergedLines],
      };
    }

    return geometryForMap;
  }, [showGeometryOnMap, mapGeometryList, missionPreviewActive, missionPreviewPaths]);

  return (
    <APIProvider apiKey={"AIzaSyArjT18S_hMt-N7NHgJSiONpXXZRZKulbg"} libraries={["drawing"]}>
      <PendingCommandsProvider
        mqttData={props.fullMQTT}
        orgId={props.orgName}
        onNavigateToMachine={navigateToMachine}
      >
        <div className="Interface">
          {/* Deere Dialog for All Stop confirmation */}
          <Dialog
            open={allStopDialogOpen}
            closeHandler={() => setAllStopDialogOpen(false)}
            title={"Warning"}
            width="sm"
          >
            <div
              style={{
                backgroundColor: "orange",
                width: "100%",
                padding: "16px",
                color: "black",
                borderRadius: "4px",
                border: "black 2px solid",
              }}
            >
              You are about to Stop all autonomous vehicles on this site. Would you like to proceed?
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 24 }}>
              <Button onClick={() => setAllStopDialogOpen(false)} disabled={allStopLoading}>
                Cancel
              </Button>
              <Button
                color="primary"
                variant="primary"
                onClick={handleAllStop}
                loading={allStopLoading}
              >
                Confirm
              </Button>
            </div>
          </Dialog>

          {/* Engineering Tools Dialog */}
          <Dialog
            open={showEngineeringTools}
            closeHandler={toggleEngineeringTools}
            title="Engineering Tools"
            width="xl"
          >
            <EngineeringTools
              fullMQTT={props.fullMQTT}
              orgName={props.orgName}
              setOrgName={setDevToolsOrgName}
              onClose={toggleEngineeringTools}
              devToolsOrgOverride={devToolsOrgOverride}
              onRevertToHeaderOrg={revertToHeaderOrg}
              headerOrgName={appContext.selectedOrganization?.name || ""}
              cabControlFullscreen={cabControlFullscreen}
              setCabControlFullscreen={setCabControlFullscreen}
            />
          </Dialog>

          {/* {isMini && <div style={{
                       width: "420px",
                       transform: `scale(${zoomPercent / 100}) translate(${translateX}px, ${translateYHeader}px)`,
                       transition: 'transform 0.2s ease', // Smooth transition
                   }}>
                       <Header orgName={orgName} setOrgName={setOrgName} fullMQTT={props.fullMQTT}
                               toggleEngineeringTools={toggleEngineeringTools}/>
                   </div>}*/}

          {/*  {!isMini &&
                       <Header orgName={orgName} setOrgName={setOrgName} fullMQTT={props.fullMQTT}
                               toggleEngineeringTools={toggleEngineeringTools}/>
                   }*/}

          {isMini && (
            <div
              className="BottomInterface"
              style={{
                height: height + "px",
                width: "420px",
                transform: showCabControl
                  ? "none"
                  : `scale(${zoomPercent / 100}) translate(${translateX}px, ${translateY}px)`,
                transition: "transform 0.2s ease", // Smooth transition
              }}
            >
              <Sidebar
                pendingNav={pendingNav}
                setPendingNav={setPendingNav}
                orgName={props.orgName}
                setOrgName={setNewOrgName}
                toggleCabControl={toggleCabControl}
                showCabControl={showCabControl}
                showEngineeringTools={showEngineeringTools}
                toggleEngineeringTools={toggleEngineeringTools}
                fullMQTT={props.fullMQTT}
                userLocation={userLocation}
                setUserLocation={setUserLocation}
                isSpoofing={isSpoofing}
                setIsSpoofing={setIsSpoofing}
                originalLocation={originalLocation}
                setOriginalLocation={setOriginalLocation}
                mapGeometryList={mapGeometryList}
                currentTopicString={currentTopicString}
                userName={props.userName}
                totalEquipmentList={totalEquipmentList}
                setGjson={setGjsonWrapper}
                setShowAllVehicles={setShowAllVehicles}
                OrgData={orgData}
                setCurJobsite={setJobsiteName}
                curJobsite={jobsiteName}
                orgDataSession={orgDataSession}
                orgDataSessionName={sessionName}
                setOrgDataSessionName={setSessionName}
                vehicleIconClicked={vehicleIconClicked}
                setVehicleIconClicked={setVehicleIconClicked}
                vehicleData={vehicleData}
                selectedFeatureId={selectedFeatureId}
                setSelectedFeatureId={setSelectedFeatureId}
                setMapGeometryList={setMapGeometryList}
                showGeometryOnMap={showGeometryOnMap}
                setShowGeometryOnMap={setShowGeometryOnMap}
                onJobsitePanelClose={handleJobsitePanelClose}
                setIsJobsiteUserSelected={setIsJobsiteUserSelected}
                missionPreviewActive={missionPreviewActive}
                setMissionPreviewActive={setMissionPreviewActive}
                missionPreviewPaths={missionPreviewPaths}
                setMissionPreviewPaths={setMissionPreviewPaths}
                missionPreviewPoints={missionPreviewPoints}
                setMissionPreviewPoints={setMissionPreviewPoints}
                followedVehicleId={followedVehicleId}
                setFollowedVehicleId={setFollowedVehicleId}
                curJobsiteIcon={undefined}
                setCurJobsiteIcon={function (newIcon: React.JSX.Element | undefined): void {
                  throw new Error("Function not implemented.");
                }}
              />
              {allStopOverlay}
              {(() => {
                const pointsForMap = showGeometryOnMap ? mapPointList : [];

                return (
                  <MapInterface
                    followedVehicleId={followedVehicleId}
                    setFollowedVehicleId={setFollowedVehicleId}
                    toggleCabControl={toggleCabControl}
                    showCabControl={showCabControl}
                    setMapInstance={setMapInstance}
                    mapInstance={mapInstance}
                    sessionName={sessionName}
                    mapPointList={pointsForMap}
                    mapGeometryList={mergedGeometryForMap}
                    orgDataSession={orgDataSession}
                    mapEquipmentList={mapEquipmentList}
                    gjson={gjson}
                    selectedFeatureId={selectedFeatureId}
                    setSelectedFeatureId={setSelectedFeatureId}
                    showGeometryOnMap={showGeometryOnMap}
                    missionPreviewActive={missionPreviewActive}
                    missionPreviewPaths={missionPreviewPaths}
                    missionPreviewPoints={missionPreviewPoints}
                  />
                );
              })()}
            </div>
          )}

          {!isMini && (
            <div className="BottomInterface">
              <Sidebar
                pendingNav={pendingNav}
                setPendingNav={setPendingNav}
                orgName={props.orgName}
                setOrgName={setNewOrgName}
                toggleCabControl={toggleCabControl}
                showCabControl={showCabControl}
                showEngineeringTools={showEngineeringTools}
                toggleEngineeringTools={toggleEngineeringTools}
                fullMQTT={props.fullMQTT}
                userLocation={userLocation}
                setUserLocation={setUserLocation}
                isSpoofing={isSpoofing}
                setIsSpoofing={setIsSpoofing}
                originalLocation={originalLocation}
                setOriginalLocation={setOriginalLocation}
                mapGeometryList={mapGeometryList}
                currentTopicString={currentTopicString}
                userName={props.userName}
                totalEquipmentList={totalEquipmentList}
                setGjson={setGjsonWrapper}
                setShowAllVehicles={setShowAllVehicles}
                OrgData={orgData}
                setCurJobsite={setJobsiteName}
                curJobsite={jobsiteName}
                orgDataSession={orgDataSession}
                orgDataSessionName={sessionName}
                setOrgDataSessionName={setSessionName}
                vehicleIconClicked={vehicleIconClicked}
                setVehicleIconClicked={setVehicleIconClicked}
                vehicleData={vehicleData}
                selectedFeatureId={selectedFeatureId}
                setSelectedFeatureId={setSelectedFeatureId}
                setMapGeometryList={setMapGeometryList}
                showGeometryOnMap={showGeometryOnMap}
                setShowGeometryOnMap={setShowGeometryOnMap}
                onJobsitePanelClose={handleJobsitePanelClose}
                setIsJobsiteUserSelected={setIsJobsiteUserSelected}
                missionPreviewActive={missionPreviewActive}
                setMissionPreviewActive={setMissionPreviewActive}
                missionPreviewPaths={missionPreviewPaths}
                setMissionPreviewPaths={setMissionPreviewPaths}
                missionPreviewPoints={missionPreviewPoints}
                setMissionPreviewPoints={setMissionPreviewPoints}
                followedVehicleId={followedVehicleId}
                setFollowedVehicleId={setFollowedVehicleId}
                curJobsiteIcon={undefined}
                setCurJobsiteIcon={function (newIcon: React.JSX.Element | undefined): void {
                  throw new Error("Function not implemented.");
                }}
              />
              {allStopOverlay}
              {(() => {
                const pointsForMap = showGeometryOnMap ? mapPointList : [];

                return (
                  <MapInterface
                    followedVehicleId={followedVehicleId}
                    setFollowedVehicleId={setFollowedVehicleId}
                    toggleCabControl={toggleCabControl}
                    showCabControl={showCabControl}
                    setMapInstance={setMapInstance}
                    mapInstance={mapInstance}
                    sessionName={sessionName}
                    mapPointList={pointsForMap}
                    mapGeometryList={mergedGeometryForMap}
                    orgDataSession={orgDataSession}
                    mapEquipmentList={mapEquipmentList}
                    gjson={gjson}
                    selectedFeatureId={selectedFeatureId}
                    showGeometryOnMap={showGeometryOnMap}
                    setSelectedFeatureId={setSelectedFeatureId}
                    missionPreviewActive={missionPreviewActive}
                    missionPreviewPaths={missionPreviewPaths}
                    missionPreviewPoints={missionPreviewPoints}
                  />
                );
              })()}
            </div>
          )}

          {/* This is a development bar. Remove before production */}
          {/* <div style={{ position: "absolute", width: "10px", height: "calc(202px + 96px)", backgroundColor: "red", top: "0px" }}></div> */}
        </div>
      </PendingCommandsProvider>
    </APIProvider>
  );
};

export default Interface;
